import DOMPurify from "dompurify";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import type { PublicMarketingBlock } from "@/lib/public-marketing";

/** Typography for CMS HTML (requires @tailwindcss/typography). */
const PROSE_ARTICLE =
  "prose prose-lg prose-gray max-w-none leading-relaxed " +
  "prose-headings:font-semibold prose-headings:text-gray-900 " +
  "prose-p:text-gray-700 prose-li:text-gray-700 " +
  "prose-a:text-primary prose-a:no-underline hover:prose-a:underline " +
  "prose-blockquote:border-l-primary prose-blockquote:text-gray-600";

function SafeHtml({ html, className = "" }: { html: string; className?: string }) {
  const clean = DOMPurify.sanitize(html, { USE_PROFILES: { html: true } });
  return (
    <div className={[PROSE_ARTICLE, className].filter(Boolean).join(" ")} dangerouslySetInnerHTML={{ __html: clean }} />
  );
}

export function MarketingBlocks({ blocks }: { blocks: PublicMarketingBlock[] }) {
  return (
    <div className="space-y-12 md:space-y-14">
      {blocks
        .slice()
        .sort((a, b) => a.sort_order - b.sort_order)
        .map((b) => {
          if (b.block_type === "hero") {
            const headline = String(b.data?.headline ?? "");
            const subheadline = String(b.data?.subheadline ?? "");
            return (
              <section key={b.id} className="pt-2 pb-2">
                <h1 className="text-3xl sm:text-4xl lg:text-[2.75rem] font-bold tracking-tight text-gray-900 leading-tight">
                  {headline}
                </h1>
                {subheadline?.trim() ? (
                  <p className="mt-5 text-lg sm:text-xl text-gray-600 max-w-3xl leading-relaxed">{subheadline}</p>
                ) : null}
              </section>
            );
          }

          if (b.block_type === "rich_text") {
            const html = String(b.data?.html ?? "");
            return (
              <section key={b.id}>
                <SafeHtml html={html} />
              </section>
            );
          }

          if (b.block_type === "two_column") {
            const left = String(b.data?.left_html ?? "");
            const right = String(b.data?.right_html ?? "");
            return (
              <section key={b.id} className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-10">
                <SafeHtml html={left} className="min-w-0" />
                <SafeHtml html={right} className="min-w-0" />
              </section>
            );
          }

          if (b.block_type === "image") {
            const asset = b.asset;
            if (!asset) return null;
            return (
              <figure key={b.id} className="not-prose space-y-3">
                <img
                  src={asset.url}
                  alt={asset.alt_text || "Marketing image"}
                  className="w-full rounded-lg border border-gray-200"
                  loading="lazy"
                />
                {asset.caption?.trim() ? (
                  <figcaption className="text-sm text-gray-500">{asset.caption}</figcaption>
                ) : null}
              </figure>
            );
          }

          if (b.block_type === "cta") {
            const label = String(b.data?.label ?? "Learn more").trim();
            const url = String(b.data?.url ?? "").trim();
            const isInternal = url.startsWith("/");
            return (
              <section key={b.id} className="not-prose">
                <Button asChild>
                  {isInternal ? (
                    <Link to={url}>{label}</Link>
                  ) : (
                    <a href={url} target="_blank" rel="noreferrer">
                      {label}
                    </a>
                  )}
                </Button>
              </section>
            );
          }

          return null;
        })}
    </div>
  );
}
